<script setup lang="ts">
  import { useI18n } from 'vue-i18n'
  import type {
    Coverage,
    CoverageOption,
    FlowPlanCoverageProps,
  } from '@/entities/Flow/components/FlowPlanCoverage/FlowPlanCoverage.types'
  import { SchemeType } from '@/shared/types/api.types'
  import IconQC from '@qicgroupdoha/qic-connect-ds/components/IconQC'
  import TooltipInsuranceValues from '@/shared/components/TooltipInsuranceValues/TooltipInsuranceValues.vue'

  const props = defineProps<FlowPlanCoverageProps>()

  const { t } = useI18n()

  const healthCoverage: CoverageOption[] = [
    {
      iconName: 'heart-pulse',
      name: t('outbound.select_plan.insurance_values.category_1.item_1.name'),
      price:
        props.plan.schemeTypeCode === SchemeType.FlyEurope
          ? t('outbound.select_plan.insurance_values.category_1.item_1.desc_flyeurope')
          : t('outbound.select_plan.insurance_values.category_1.item_1.desc'),
      tooltip: t('outbound.select_plan.insurance_values.category_1.item_1.tooltip_desc'),
    },
    {
      iconName: 'teeth',
      name: t('outbound.select_plan.insurance_values.category_1.item_2.name'),
      price: t('outbound.select_plan.insurance_values.category_1.item_2.desc'),
    },
    {
      iconName: 'skiing',
      name: t('outbound.select_plan.insurance_values.category_1.item_3.name'),
      price: t('outbound.select_plan.insurance_values.addon'),
      notAvailableFor: [SchemeType.FlyEurope],
    },
    {
      iconName: 'virus',
      name: t('outbound.select_plan.insurance_values.category_1.item_4.name'),
      price: t('outbound.select_plan.insurance_values.addon'),
      notAvailableFor: [SchemeType.Regional, SchemeType.FlyEurope],
    },
  ]

  const belongingsCoverage: CoverageOption[] = [
    {
      iconName: 'luggage',
      name: t('outbound.select_plan.insurance_values.category_2.item_1.name'),
      price: t('outbound.select_plan.insurance_values.category_2.item_1.desc'),
      notAvailableFor: [SchemeType.FlyEurope],
    },
    {
      iconName: 'passport',
      name: t('outbound.select_plan.insurance_values.category_2.item_2.name'),
      price: t('outbound.select_plan.insurance_values.category_2.item_2.desc'),
      notAvailableFor: [SchemeType.FlyEurope],
    },
    {
      iconName: 'bicycle',
      name: t('outbound.select_plan.insurance_values.category_2.item_3.name'),
      price: t('outbound.select_plan.insurance_values.category_2.item_3.desc'),
      notAvailableFor: [SchemeType.FlyEurope],
    },
  ]

  const tripCoverage: CoverageOption[] = [
    {
      iconName: 'plane',
      name: t('outbound.select_plan.insurance_values.category_3.item_1.name'),
      price: t('outbound.select_plan.insurance_values.category_3.item_1.desc'),
      notAvailableFor: [SchemeType.FlyEurope],
    },
    {
      iconName: 'shield-user',
      name: t('outbound.select_plan.insurance_values.category_3.item_2.name'),
      price: t('outbound.select_plan.insurance_values.category_3.item_2.desc'),
      notAvailableFor: [SchemeType.FlyEurope],
    },
    {
      iconName: 'scales',
      name: t('outbound.select_plan.insurance_values.category_3.item_3.name'),
      price: t('outbound.select_plan.insurance_values.category_3.item_3.desc'),
      notAvailableFor: [SchemeType.FlyEurope],
    },
    {
      iconName: 'clock',
      name: t('outbound.select_plan.insurance_values.category_3.item_4.name'),
      price: t('outbound.select_plan.insurance_values.category_3.item_4.desc'),
      notAvailableFor: [SchemeType.FlyEurope],
    },
  ]

  const coverage: Coverage[] = [
    {
      label: t('outbound.select_plan.insurance_values.category_1.label'),
      options: healthCoverage,
    },
    {
      label: t('outbound.select_plan.insurance_values.category_2.label'),
      options: belongingsCoverage,
    },
    {
      label: t('outbound.select_plan.insurance_values.category_3.label'),
      options: tripCoverage,
    },
  ]
</script>

<template>
  <div class="coverage">
    <div v-for="{ label, options } in coverage" :key="label" class="coverage__block">
      <div
        :class="[
          'coverage__block-title',
          {
            'coverage__block-title--disabled': options.every((option) =>
              option.notAvailableFor?.includes(plan.schemeTypeCode)
            ),
          },
        ]"
      >
        {{ label }}
      </div>
      <div
        v-for="{ name, price, iconName, notAvailableFor, tooltip } in options"
        :key="name"
        class="coverage__block-item"
      >
        <IconQC
          v-if="notAvailableFor?.includes(plan.schemeTypeCode)"
          name="x-close"
          :size="20"
          color="#526C8266"
        />
        <IconQC v-else :name="iconName" :size="20" class="coverage__item-icon" color="#394B5ACC" />
        <span
          :class="[
            'coverage__item-description',
            {
              'coverage__item-description--disabled': notAvailableFor?.includes(
                plan.schemeTypeCode
              ),
            },
          ]"
        >
          {{ name }}
          <TooltipInsuranceValues v-if="tooltip" :info="tooltip" />
        </span>
        <span v-if="!notAvailableFor?.includes(plan.schemeTypeCode)" class="coverage__item-price">
          {{ price }}
        </span>
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
  @use '@/shared/assets/styles/mixins' as *;
  @use '@qicgroupdoha/qic-connect-ds/scss/typography';

  .coverage {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: var(--spacing-5);
    align-self: stretch;

    &__block-title {
      @include typography.extra-bonus-default-medium;

      display: flex;
      padding-bottom: var(--spacing-3);
      align-items: center;

      /* qic-connect-ignore-next-line */
      gap: 10px;
      align-self: stretch;

      &--disabled {
        /* qic-connect-ignore-next-line */
        color: rgb(82 108 130 / 40%);
      }
    }

    &__block {
      display: flex;
      flex-direction: column;
      align-items: flex-start;
      align-self: stretch;
    }

    &__block-item {
      @include typography.text-small-medium;

      display: flex;
      padding: var(--spacing-2) 0;
      align-items: flex-start;
      gap: var(--spacing-4);
      align-self: stretch;

      @include mobile-flow {
        gap: var(--spacing-4);
      }
    }

    &__item-icon {
      margin-top: var(--spacing-1);
    }

    &__item-description {
      flex: 1 0 0;

      &--disabled {
        /* qic-connect-ignore-next-line */
        color: rgb(82 108 130 / 40%);
        text-decoration-line: line-through;
      }

      @include mobile-flow {
        margin-inline-start: calc(-1 * var(--spacing-2));
        max-width: 44%;
      }
    }

    &__item-price {
      display: flex;
      align-items: flex-end;
      gap: var(--spacing-2);

      @include mobile-flow {
        margin-inline-start: auto;
      }
    }
  }

  .rtl {
    .coverage {
      &__item-description {
        @include mobile-flow {
          &:nth-of-type(1) {
            max-width: 41%;
          }
        }
      }
    }
  }
</style>
